import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FavoritesContext = createContext(null);

export const FavoritesProvider = ({ children }) => {
  const { user, token } = useAuth();
  const isFirstRender = useRef(true);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load favorites based on auth state
  useEffect(() => {
    if (user && token) {
      // User is logged in - load from API
      loadFavoritesFromAPI();
    } else {
      // Not logged in - load from localStorage
      loadFavoritesFromStorage();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  const loadFavoritesFromStorage = () => {
    const stored = localStorage.getItem('favorites');
    if (stored) {
      try {
        setFavorites(JSON.parse(stored));
      } catch {
        setFavorites([]);
      }
    }
  };

  const loadFavoritesFromAPI = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/v2/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(res.data.map(b => ({
        id: b.id,
        nome: b.nome,
        categoria: b.categoria,
        bairro: b.bairro,
        foto: b.avatar_url,
        media_avaliacoes: b.rating_avg
      })));
    } catch (error) {
      console.error('Erro ao carregar favoritos:', error);
      // Fallback to localStorage if API fails
      loadFavoritesFromStorage();
    } finally {
      setLoading(false);
    }
  };

  // Save to localStorage for non-logged users
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!user) {
      localStorage.setItem('favorites', JSON.stringify(favorites));
    }
  }, [favorites, user]);

  const addFavorite = useCallback(async (business) => {
    const businessData = {
      id: business.id,
      nome: business.nome,
      categoria: business.categoria,
      bairro: business.bairro,
      foto: business.foto || business.avatar_url,
      media_avaliacoes: business.media_avaliacoes || business.rating_avg,
      addedAt: new Date().toISOString()
    };

    // Optimistic update
    setFavorites(prev => {
      if (prev.some(f => f.id === business.id)) return prev;
      return [...prev, businessData];
    });

    if (user && token) {
      try {
        await axios.post(`${API}/v2/favorites/${business.id}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        // Revert on error
        if (error.response?.status !== 400) { // 400 = already favorited
          setFavorites(prev => prev.filter(f => f.id !== business.id));
        }
        console.error('Erro ao adicionar favorito:', error);
      }
    }
  }, [user, token]);

  const removeFavorite = useCallback(async (businessId) => {
    // Store previous state for rollback
    const previousFavorites = favorites;
    
    // Optimistic update
    setFavorites(prev => prev.filter(f => f.id !== businessId));

    if (user && token) {
      try {
        await axios.delete(`${API}/v2/favorites/${businessId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        // Revert on error
        setFavorites(previousFavorites);
        console.error('Erro ao remover favorito:', error);
      }
    }
  }, [user, token, favorites]);

  const toggleFavorite = useCallback((business) => {
    if (favorites.some(f => f.id === business.id)) {
      removeFavorite(business.id);
    } else {
      addFavorite(business);
    }
  }, [favorites, addFavorite, removeFavorite]);

  const isFavorite = useCallback((businessId) => {
    return favorites.some(f => f.id === businessId);
  }, [favorites]);

  // Sync localStorage favorites to API when user logs in
  const syncLocalFavoritesToAPI = useCallback(async () => {
    if (!user || !token) return;
    
    const stored = localStorage.getItem('favorites');
    if (stored) {
      try {
        const localFavorites = JSON.parse(stored);
        for (const fav of localFavorites) {
          try {
            await axios.post(`${API}/v2/favorites/${fav.id}`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } catch (e) {
            // Ignore errors (already favorited, etc)
          }
        }
        // Clear localStorage after sync
        localStorage.removeItem('favorites');
        // Reload from API
        loadFavoritesFromAPI();
      } catch (e) {
        console.error('Erro ao sincronizar favoritos:', e);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  // Sync when user logs in
  useEffect(() => {
    if (user && token) {
      syncLocalFavoritesToAPI();
    }
  }, [user, token, syncLocalFavoritesToAPI]);

  const value = useMemo(() => ({
    favorites,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    isFavorite,
    count: favorites.length,
    loading
  }), [favorites, addFavorite, removeFavorite, toggleFavorite, isFavorite, loading]);

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within FavoritesProvider');
  }
  return context;
};
